import { useEffect, useState } from "react";
import { useLocation, useMatch, useNavigate } from "react-router-dom";
import {
  useBreakpoints,
  LoadingShim,
  LoadingSkeleton,
} from "@narmi/design_system";
import { useLocalization } from "@fluent/react";
import { catcat as cc } from "cerulean";
import { entities } from "byzantine";
import {
  FormattedBulkPayment,
  useBulkPaymentsTotal,
  useDeleteBulkPayment,
  useFetchPaginatedBulkPayments,
  useFormattedPaginatedBulkPayments,
} from "byzantine/src/dbbl/businessLogic/entities/bulkPayments";
import PageNavigation from "../../page_navigation/PageNavigation";
import { SudoProvider } from "../../SudoContext";
import { EmptyState, SearchBar, CancelPaymentDialog } from "../common";
import BulkPaymentTable from "./BulkPaymentTable";
import BulkReverseFlow from "./BulkReverseFlow";
import usePaymentActivityContainer from "../hooks";
import { FilterChips } from "../Filters/filterConfig";
import FilterContainer from "../Filters/FilterContainer";
import {
  PAYMENT_ACTIVITY_ROUTE,
  getBulkReverseRoute,
  getBulkReverseFromDrawerRoute,
} from "../routes";

const BulkPaymentContainer = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { m, l } = useBreakpoints();
  const { l10n } = useLocalization();
  const form = entities.bulkPayments.BulkPaymentForm.useForm();
  const [cancelTargetId, setCancelTargetId] =
    useState<API.BulkPaymentId | null>(null);
  const { deleteBulkPayment } = useDeleteBulkPayment();

  // "reverse all eligible" — table entry
  const reverseFromTableMatch = useMatch(
    `${getBulkReverseRoute(":reverseBulkPaymentId")}/*`
  );
  // "reverse all eligible" — drawer entry
  const reverseFromDrawerMatch = useMatch(
    `${getBulkReverseFromDrawerRoute(":bulkPaymentId")}/*`
  );
  const reverseBulkPaymentId =
    reverseFromTableMatch?.params.reverseBulkPaymentId ??
    reverseFromDrawerMatch?.params.bulkPaymentId ??
    null;
  const reverseFromDrawer = Boolean(reverseFromDrawerMatch);

  const {
    state,
    formattedPaginatedPayments,
    paymentsLoading,
    bootstrapLoading,
    pagination,
    handleSearchChange,
    handleReset,
    handleSetFilters,
    refetchCurrentPage,
  } = usePaymentActivityContainer({
    useFetchPaginated: useFetchPaginatedBulkPayments,
    useFormattedPaginated: useFormattedPaginatedBulkPayments,
    useTotal: useBulkPaymentsTotal,
    useBootstrap: entities.bulkPayments.bootstrap.useBootstrap,
  });

  useEffect(() => {
    if (location.pathname.endsWith("/bulk")) {
      form.resetForm();
    }
  }, [location.pathname]);

  const toggleCancelDialog = (uuid?: API.BulkPaymentId) => {
    setCancelTargetId(uuid ?? null);
  };

  const handleCancelPayment = () => {
    if (!cancelTargetId) return;

    deleteBulkPayment(cancelTargetId, {
      onSuccess: () => {
        refetchCurrentPage();
        toggleCancelDialog();
      },
    });
  };

  const renderBulkTable = () => {
    if (paymentsLoading) return <LoadingShim isLoading />;
    if (formattedPaginatedPayments.length === 0) {
      return <EmptyState state={state} handleReset={handleReset} />;
    }

    return (
      <div className={cc({ "margin--top--l": !(m || l) })}>
        <BulkPaymentTable
          payments={formattedPaginatedPayments as FormattedBulkPayment[]}
          handleToggleCancelDialog={toggleCancelDialog}
        />
        <PageNavigation
          pagination={pagination}
          marginClasses="margin--all--l"
        />
      </div>
    );
  };

  return (
    <LoadingSkeleton isLoading={bootstrapLoading}>
      <div className="margin--top--xl">
        <SearchBar
          handleSearchChange={handleSearchChange}
          searchValue={state.searchValue}
          placeholder={l10n.getString("payment-activity-search-bulk-label")}
        />
      </div>
      <div>
        <FilterContainer
          setFilters={handleSetFilters}
          filters={state.filters}
          allowedFilters={[
            FilterChips.DATE,
            FilterChips.BULK_STATUS,
            FilterChips.AMOUNT,
            FilterChips.ACCOUNT,
            FilterChips.TEMPLATE,
          ]}
          parentSelectableFilters={[
            FilterChips.BULK_STATUS,
            FilterChips.AMOUNT,
            FilterChips.ACCOUNT,
            FilterChips.TEMPLATE,
          ]}
        />
      </div>
      {renderBulkTable()}
      {cancelTargetId && (
        <CancelPaymentDialog
          isOpen={Boolean(cancelTargetId)}
          handleClose={() => toggleCancelDialog()}
          handleCancel={handleCancelPayment}
          isBulk={true}
        />
      )}
      {reverseBulkPaymentId && (
        <SudoProvider
          basename={
            reverseFromDrawer
              ? getBulkReverseFromDrawerRoute(reverseBulkPaymentId)
              : getBulkReverseRoute(reverseBulkPaymentId)
          }
        >
          <BulkReverseFlow
            bulkPaymentId={reverseBulkPaymentId}
            reverseRoute={
              reverseFromDrawer
                ? getBulkReverseFromDrawerRoute(reverseBulkPaymentId)
                : getBulkReverseRoute(reverseBulkPaymentId)
            }
            dismissRoute={
              reverseFromDrawer
                ? `/${PAYMENT_ACTIVITY_ROUTE}/bulk/${reverseBulkPaymentId}`
                : `/${PAYMENT_ACTIVITY_ROUTE}/bulk`
            }
            onComplete={() => {
              navigate(`/${PAYMENT_ACTIVITY_ROUTE}/bulk`);
            }}
          />
        </SudoProvider>
      )}
    </LoadingSkeleton>
  );
};

export default BulkPaymentContainer;
